import {
    MIRROR_STORAGE_KEY,
    type AuthProvider,
    type LocalStorageTokenData,
    type SessionContext,
} from '../types'

const LOGGED_OUT: SessionContext = { isAuthenticated: false }

const NON_ANONYMOUS_PROVIDERS: ReadonlySet<string> = new Set<AuthProvider>([
    'apple',
    'cognito',
    'facebook',
    'google',
])

// Heuristic boundary between Unix *seconds* and *milliseconds* — any plausible
// real timestamp in seconds is below this; in ms it is above.
const SECONDS_MS_BOUNDARY = 1e12

/**
 * Resolve the mirror's `authTokenExp` to an epoch-ms instant, or null when absent /
 * unparseable. The real cross-app mirror writes an **ISO-8601 string** (ui-react/canopy);
 * a numeric Unix timestamp in seconds or ms is also accepted.
 */
function expiresAtMs(exp: unknown): number | null {
    if (typeof exp === 'number' && Number.isFinite(exp)) {
        return exp < SECONDS_MS_BOUNDARY ? exp * 1000 : exp
    }
    if (typeof exp === 'string') {
        const parsed = Date.parse(exp)
        return Number.isNaN(parsed) ? null : parsed
    }
    return null
}

/**
 * Read the cross-app localStorage `userAccessToken` mirror into a client-safe
 * `SessionContext`. Logged-in only when the entry has a non-anonymous provider,
 * a non-empty token, and an unexpired `authTokenExp`.
 * `sessionId` is carried when present but is not part of the gate. Absent, malformed,
 * anonymous, or expired
 * entries resolve to logged-out. Never throws
 * (private-mode `SecurityError`, quota, or parse failures degrade to logged-out).
 *
 * The returned context never carries the token — that stays in the mirror.
 */
export function readMirror(nowMs: number = Date.now()): SessionContext {
    let raw: string | null
    try {
        raw = localStorage.getItem(MIRROR_STORAGE_KEY)
    } catch {
        return LOGGED_OUT
    }
    if (!raw) {
        return LOGGED_OUT
    }

    let data: Record<string, unknown>
    try {
        data = JSON.parse(raw) as Record<string, unknown>
    } catch {
        return LOGGED_OUT
    }

    const { authToken, authTokenExp, provider, sessionId, email } = data

    if (typeof authToken !== 'string' || !authToken) {
        return LOGGED_OUT
    }
    if (typeof provider !== 'string' || !NON_ANONYMOUS_PROVIDERS.has(provider)) {
        return LOGGED_OUT
    }
    const expMs = expiresAtMs(authTokenExp)
    if (expMs === null || expMs <= nowMs) {
        return LOGGED_OUT
    }

    // The name is not derived here — it is fetched post-hydration from userinfo.json
    // for every provider and merged onto the session by SessionProvider.
    return {
        isAuthenticated: true,
        provider: provider as AuthProvider,
        ...(typeof sessionId === 'string' && sessionId ? { sessionId } : {}),
        ...(typeof email === 'string' ? { email } : {}),
    }
}

/**
 * Read the full raw mirror entry — including expired tokens — for the restore path
 * (`attemptSessionRestore` needs the expired token + email to refresh). Unlike
 * `readMirror`, this applies **no freshness gate**: it returns whatever is stored, or
 * null when absent/malformed. Never throws.
 */
export function readMirrorEntry(): LocalStorageTokenData | null {
    let raw: string | null
    try {
        raw = localStorage.getItem(MIRROR_STORAGE_KEY)
    } catch {
        return null
    }
    if (!raw) {
        return null
    }
    try {
        const data = JSON.parse(raw) as Partial<LocalStorageTokenData>
        if (typeof data.authToken !== 'string' || typeof data.provider !== 'string') {
            return null
        }
        return {
            authToken: data.authToken,
            authTokenExp:
                typeof data.authTokenExp === 'number' || typeof data.authTokenExp === 'string'
                    ? data.authTokenExp
                    : null,
            email: typeof data.email === 'string' ? data.email : null,
            identityId: typeof data.identityId === 'string' ? data.identityId : null,
            provider: data.provider,
            sessionId: typeof data.sessionId === 'string' ? data.sessionId : '',
        }
    } catch {
        return null
    }
}

// --- useSyncExternalStore adapters -------------------------------------------
// Cache the derived context keyed on the raw mirror string so getSnapshot
// returns a referentially stable value (required by useSyncExternalStore).

let cachedRaw: string | null | undefined
let cachedContext: SessionContext = LOGGED_OUT

/** Stable client snapshot of the session, recomputed only when the mirror string changes. */
export function getSessionSnapshot(): SessionContext {
    let raw: string | null
    try {
        raw = localStorage.getItem(MIRROR_STORAGE_KEY)
    } catch {
        return LOGGED_OUT
    }
    if (raw === cachedRaw) {
        return cachedContext
    }
    cachedRaw = raw
    cachedContext = readMirror()
    return cachedContext
}

/** Server snapshot — always logged-out, so SSR/SSG HTML is session-blind. */
export function getServerSessionSnapshot(): SessionContext {
    return LOGGED_OUT
}

// Same-tab change notification. The `storage` event only fires in *other* tabs,
// so in-tab mutations (logout clearing the mirror) must notify subscribers directly.
const listeners = new Set<() => void>()

function notifyMirrorChanged(): void {
    for (const listener of listeners) {
        listener()
    }
}

/**
 * Subscribe to mirror changes — both cross-tab (`storage` event) and same-tab
 * (`clearMirror`/in-tab writes via `notifyMirrorChanged`). Returns an unsubscribe.
 */
export function subscribeToMirror(onChange: () => void): () => void {
    listeners.add(onChange)
    if (typeof window !== 'undefined') {
        window.addEventListener('storage', onChange)
    }
    return () => {
        listeners.delete(onChange)
        if (typeof window !== 'undefined') {
            window.removeEventListener('storage', onChange)
        }
    }
}

/**
 * Read the raw session token from the mirror (for client-direct WAPI calls such
 * as logout/refresh). Returns null when absent/malformed. Never throws. This is the
 * one place the token leaves the mirror — it never reaches `SessionContext`.
 */
export function readMirrorToken(): string | null {
    let raw: string | null
    try {
        raw = localStorage.getItem(MIRROR_STORAGE_KEY)
    } catch {
        return null
    }
    if (!raw) {
        return null
    }
    try {
        const data = JSON.parse(raw) as Record<string, unknown>
        return typeof data.authToken === 'string' && data.authToken ? data.authToken : null
    } catch {
        return null
    }
}

/**
 * Write the cross-app `userAccessToken` mirror (on a federated login / refresh) and
 * notify same-tab subscribers so the navbar flips in place. `authTokenExp` is normalized
 * to an **ISO-8601 string** so the entry is byte-compatible with what ui-react/canopy
 * write — ui-react validates with `moment(authTokenExp)`, which misreads a bare
 * numeric-seconds value as a 1970 date (→ treats the session as logged-out). Never throws.
 */
export function writeMirror(entry: LocalStorageTokenData): void {
    const ms = expiresAtMs(entry.authTokenExp)
    const normalized: LocalStorageTokenData = {
        ...entry,
        authTokenExp: ms === null ? null : new Date(ms).toISOString(),
    }
    try {
        localStorage.setItem(MIRROR_STORAGE_KEY, JSON.stringify(normalized))
    } catch {
        // private-mode / quota — non-fatal
    }
    notifyMirrorChanged()
}

/** Remove the mirror (logout) and notify same-tab subscribers. Never throws. */
export function clearMirror(): void {
    try {
        localStorage.removeItem(MIRROR_STORAGE_KEY)
    } catch {
        // private-mode / quota — non-fatal
    }
    notifyMirrorChanged()
}
